/**
 * Format a `Date` as `YYYY-MM-DD` from its **local** calendar components.
 *
 * Deliberately NOT `toISOString().slice(0, 10)`. `toISOString()` converts to UTC
 * first, so slicing the date part off a local-midnight Date yields the PREVIOUS
 * day in every timezone east of UTC. Scrips clinics run Asia/Dubai (UTC+4), so
 * that path is wrong for the whole business day, not just near midnight:
 *
 *   new Date(2026, 7, 26)              // Aug 26 00:00 local (+04)
 *     .toISOString()                   // "2026-08-25T20:00:00.000Z"
 *     .slice(0, 10)                    // "2026-08-25"   <- a day early
 *
 *   ymdLocal(new Date(2026, 7, 26))    // "2026-08-26"   <- correct
 *
 * Use this wherever a Date is a *calendar day* the user picked, rather than an
 * instant: query params, form values, cache keys, native `<input type="date">`.
 * For a genuine instant (an appointment start, an audit timestamp) keep the full
 * ISO string with its offset — do not reduce it to a day.
 */
export function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Today as `YYYY-MM-DD` in the user's **local** calendar.
 *
 * A named home for the single most repeated instance of the bug above. `todayIso()`-shaped
 * private helpers doing `new Date().toISOString().slice(0, 10)` have now been found and fixed
 * in four separate places (#755 scheduling ×4, #759 consent + problems chart + account
 * details). Each was module-private, so each needed its own fix and none could be covered by
 * a test without mounting the component around it — which is why PROD-1749's fix shipped with
 * no regression guard despite writing a clinical date.
 *
 * Import this instead of re-deriving it. One implementation, one test (PROD-1750).
 */
export function todayLocalYmd(): string {
  return ymdLocal(new Date());
}
